import React, { useState } from 'react';
import styled from 'styled-components';

import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { TotemSelect } from 'components/TotemSelect';

import { handleUpdateActionEndToast } from 'pages/SupplyOrders/shared/handleUpdateActionEndToast';
import { TotemInput } from 'components/TotemInput';

import { useMarkSupplyOrderAsAbortedMutation, TotadminPermission } from 'data/__generated__';

import {
    ABORT_OTHER_REASON_VALUE,
    ABORT_WITHOUT_REASON_LABEL,
    STATE_CHANGE_REASONS,
} from 'pages/SupplyOrders/shared/abortReasons';

export function AbortSupplyOrderPopup({
    isOpen,
    setIsOpen,
    supplyOrderId,
}: {
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
    supplyOrderId: string;
}) {
    const [selectedAbortingReasonOption, setSelectedAbortingReasonOption] = useState<string | null>(null);
    const [otherAbortingReason, setOtherAbortingReason] = useState<string | undefined>(undefined);
    const [markSupplyOrderAsAborted, { loading: markSupplyOrderAsAbortedLoading }] =
        useMarkSupplyOrderAsAbortedMutation({
            variables: {
                supplyOrderIdsWithReasons: [
                    {
                        supplyOrderId,
                        stateChangeReasons:
                            selectedAbortingReasonOption === ABORT_OTHER_REASON_VALUE
                                ? [otherAbortingReason || ABORT_WITHOUT_REASON_LABEL]
                                : [selectedAbortingReasonOption || ABORT_WITHOUT_REASON_LABEL],
                    },
                ],
            },
        });

    async function handleSubmit() {
        const { data } = await markSupplyOrderAsAborted();

        if (!data) {
            throw new Error("Une erreur est survenue lors de l'annulation de l'achat");
        }

        const {
            markSupplyOrdersAsAborted: { updatedSupplyOrders, errors, warnings },
        } = data;
        handleUpdateActionEndToast({
            displaySuccess: !!updatedSupplyOrders.length,
            successText: "L'achat a bien été annulé",
            errors,
            warnings,
        });
        setIsOpen(false);
    }

    return (
        <TotemPopup
            title="Souhaitez-vous vraiment annuler l'achat ?"
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            contentOverflow="visible"
        >
            <Container>
                <SubmitContainer>
                    {selectedAbortingReasonOption === ABORT_OTHER_REASON_VALUE ? (
                        <>
                            <TotemInput
                                label="Autre raison"
                                onChange={setOtherAbortingReason}
                                placeholder="Autre raison de l'annulation de l'achat"
                                value={otherAbortingReason}
                                autoFocus
                            />
                            <ValidationButton onClick={() => setSelectedAbortingReasonOption(null)}>
                                Choisir une raison de la liste
                            </ValidationButton>
                        </>
                    ) : (
                        <TotemSelect
                            label="Raison de l'annulation"
                            placeholder="Indiquer la raison de l'annulation de l'achat"
                            value={selectedAbortingReasonOption}
                            options={STATE_CHANGE_REASONS}
                            onChange={setSelectedAbortingReasonOption}
                        />
                    )}

                    <ValidationLoadingButton
                        disabled={
                            !selectedAbortingReasonOption ||
                            (selectedAbortingReasonOption === ABORT_OTHER_REASON_VALUE && !otherAbortingReason)
                        }
                        loading={markSupplyOrderAsAbortedLoading}
                        useIcon={false}
                        label="Valider l'annulation"
                        onClick={handleSubmit}
                        requiredPermissions={[TotadminPermission.SupplyOrdersManage]}
                    />
                </SubmitContainer>
            </Container>
        </TotemPopup>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    & > :not(:first-child) {
        margin-top: 15px;
    }
`;

const SubmitContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: 15px;
    width: 100%;
`;

const ValidationButton = styled(TotemPrimaryButton)`
    margin-top: 15px;
`;

const ValidationLoadingButton = styled(TotemPrimaryLoadingButton)`
    margin-top: 15px;
`;
